/**
 * Enterprise SEO config – Mrs Cater customer app.
 * Comprehensive keywords, meta, and structured data for South Africa event catering.
 */

const DEFAULT_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_URL
    ? import.meta.env.VITE_APP_URL.replace(/\/$/, '')
    : typeof window !== 'undefined'
      ? window.location.origin
      : 'https://mrscater.com';

// —— Primary brand & category ——
const PRIMARY = [
  'Mrs Cater',
  'catering South Africa',
  'event catering South Africa',
  'wedding catering South Africa',
  'funeral catering South Africa',
  'corporate catering South Africa',
  'food delivery South Africa',
  'event food delivery',
  'book caterer South Africa',
  'find caterers South Africa',
  'verified caterers South Africa',
  'restaurant catering South Africa',
  'bulk food order South Africa',
  'Uber Eats South Africa',
  'Mr D Food South Africa',
  'Uber Eats alternative events',
  'Mr D Food alternative events',
  'Uber Eats for events',
  'Mr D Food for events',
  'restaurant delivery for events',
  'food delivery for weddings',
  'food delivery for funerals',
  'food delivery for corporate events',
  'caterers near me',
  'caterers near me South Africa',
  'catering companies South Africa',
  'event catering companies',
  'party catering South Africa',
  'conference catering South Africa',
  'office catering South Africa',
  'traditional catering South Africa',
  'African catering South Africa',
  'cultural catering',
  'Lobola catering',
  'birthday catering',
  'celebration catering',
  'memorial catering',
  'church catering',
  'school catering',
  'government catering',
  'NGO catering',
  'festival catering',
  'marquee catering',
  'outside catering',
  'in-house catering',
  'drop-off catering',
  'full-service catering',
  'buffet catering',
  'plated catering',
  'canape catering',
  'halal catering South Africa',
  'vegetarian catering',
  'vegan catering',
  'kosher catering',
];

// —— Geo (cities & regions) ——
const GEO = [
  'catering Johannesburg',
  'catering Cape Town',
  'catering Durban',
  'catering Pretoria',
  'catering Port Elizabeth',
  'catering Bloemfontein',
  'catering East London',
  'catering Pietermaritzburg',
  'catering Nelspruit',
  'catering Polokwane',
  'catering Kimberley',
  'catering Rustenburg',
  'catering Soweto',
  'catering Sandton',
  'catering Centurion',
  'catering Midrand',
  'catering Roodepoort',
  'catering Stellenbosch',
  'catering Franschhoek',
  'catering Paarl',
  'catering Umhlanga',
  'catering Ballito',
  'catering Richards Bay',
  'catering East Rand',
  'catering West Rand',
  'catering Tshwane',
  'catering eThekwini',
  'catering Buffalo City',
  'catering Nelson Mandela Bay',
  'wedding catering Johannesburg',
  'wedding catering Cape Town',
  'wedding catering Durban',
  'wedding catering Pretoria',
  'funeral catering Johannesburg',
  'funeral catering Cape Town',
  'corporate catering Johannesburg',
  'corporate catering Cape Town',
  'corporate catering Sandton',
  'event caterers Johannesburg',
  'event caterers Cape Town',
  'find caterers Johannesburg',
  'find caterers Cape Town',
  'book caterer Durban',
  'book caterer Pretoria',
];

// —— Long-tail & intent ——
const LONG_TAIL = [
  'order food for wedding South Africa',
  'order food for funeral South Africa',
  'catering for 50 guests',
  'catering for 100 guests',
  'catering for 200 guests',
  'last minute catering South Africa',
  'affordable wedding catering',
  'affordable funeral catering',
  'corporate event catering Johannesburg',
  'conference lunch catering',
  'team building catering',
  'year end function catering',
  'product launch catering',
  'gala dinner catering',
  'cocktail party catering',
  'baby shower catering',
  'bridal shower catering',
  'engagement party catering',
  'anniversary catering',
  'retirement function catering',
  'graduation party catering',
  'traditional wedding food',
  'African wedding catering',
  'Indian wedding catering South Africa',
  'white wedding catering',
  'reception catering',
  'catering quote South Africa',
  'catering prices South Africa',
  'compare caterers South Africa',
  'best caterers South Africa',
  'top caterers Johannesburg',
  'top caterers Cape Town',
  'trusted caterers South Africa',
  'rated caterers South Africa',
  'food delivery events same day',
  'bulk food delivery',
  'hot food delivery events',
  'cold buffet delivery',
  'set up and serve catering',
  'waitrons and catering',
  'crockery hire and catering',
  'event planning and catering',
];

// —— Competitor & alternative ——
const ALTERNATIVES = [
  'Uber Eats',
  'Mr D Food',
  'Uber Eats alternative',
  'Mr D Food alternative',
  'Mr D Food for events',
  'Uber Eats for events',
  'restaurant delivery app South Africa',
  'food delivery app events',
  'order food online South Africa',
  'order catering online',
  'online catering booking',
  'catering marketplace South Africa',
  'catering platform South Africa',
];

// Single string for meta keywords (use first 200–250 terms to stay reasonable in size)
const ALL_KEYWORDS = [
  ...PRIMARY,
  ...GEO,
  ...LONG_TAIL,
  ...ALTERNATIVES,
];
const META_KEYWORDS_STRING = ALL_KEYWORDS.join(', ');

export const SEO = {
  baseUrl: DEFAULT_BASE,
  siteName: 'Mrs Cater',
  locale: 'en_ZA',
  twitterHandle: '@mrscater',

  defaultTitle:
    'Find verified caterers or restaurants for your events | Mrs Cater',
  defaultDescription:
    'Find and book verified caterers and restaurants for weddings, funerals, corporate events and more across South Africa. Mrs Cater – event catering made simple.',
  defaultImage: '/assets/images/catering.png',

  /** Full comma-separated keyword string for meta keywords (enterprise set). */
  keywords: META_KEYWORDS_STRING,

  /** Arrays for programmatic use (e.g. JSON-LD, internal search). */
  keywordsPrimary: PRIMARY,
  keywordsGeo: GEO,
  keywordsLongTail: LONG_TAIL,
  keywordsAlternatives: ALTERNATIVES,
  keywordsAll: ALL_KEYWORDS,
};

/** Base JSON-LD: Organization, WebSite (with SearchAction), LocalBusiness. */
export function getBaseJsonLd() {
  const base = DEFAULT_BASE;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SEO.siteName,
      url: base,
      description: SEO.defaultDescription,
      areaServed: { '@type': 'Country', name: 'South Africa' },
      sameAs: [],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SEO.siteName,
      url: base,
      description: SEO.defaultDescription,
      inLanguage: 'en-ZA',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${base}/caterer-discovery?location={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${base}/#localbusiness`,
      name: SEO.siteName,
      description:
        'Catering marketplace and restaurant-style food delivery for events in South Africa. Book verified caterers for weddings, funerals, corporate and traditional events. Alternative to Uber Eats and Mr D Food for event catering.',
      url: base,
      areaServed: [
        { '@type': 'Country', name: 'South Africa' },
        { '@type': 'City', name: 'Johannesburg' },
        { '@type': 'City', name: 'Cape Town' },
        { '@type': 'City', name: 'Durban' },
        { '@type': 'City', name: 'Pretoria' },
      ],
      servesCuisine: [
        'African',
        'South African',
        'Traditional',
        'Contemporary',
        'Wedding catering',
        'Funeral catering',
        'Corporate catering',
      ],
    },
  ];
}

export default SEO;
